import { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import { Trans, useTranslation } from 'react-i18next';
import { FORM_ERROR } from 'final-form';
import { makeValidate, TextField } from 'mui-rff';
import * as Yup from 'yup';
import { Alert, Box, Button, Card, CardContent, InputLabel, LinearProgress } from '@mui/material';

import { MyToverAPI } from 'api/MyToverAPI';

interface FormData {
  serial: string;
}

const GetConfigurationUpdateForm = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [serial, setSerial] = useState('');
  const [updateURL, setUpdateURL] = useState('');

  const schema = Yup.object({
    serial: Yup.string().required(),
  });
  const validate = makeValidate(schema);

  const onSubmit = async ({ serial }: FormData) => {
    try {
      setLoading(true);
      const { success, parsed } = await MyToverAPI.getConfigurationLink(serial);
      // @ts-ignore-next-line Toverzicht API is different from v1,2,3
      if (success === 'ERR') {
        return { [FORM_ERROR]: t('error.unspecified') };
      }
      if (parsed.data.url) setUpdateURL(parsed.data.url);
    } catch (error) {
      return { [FORM_ERROR]: t('error.unspecified') };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading) setUpdateURL('');
  }, [loading]);

  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: 'grey.100',
        border: '1px solid',
        borderColor: 'grey.300',
      }}
    >
      <CardContent>{t('service-center.get-update.serial.intro')}</CardContent>
      <CardContent>
        <Form
          onSubmit={onSubmit}
          initialValues={{ serial: '' }}
          validate={validate}
          render={({ handleSubmit, submitError, dirtySinceLastSubmit }) => (
            <form onSubmit={handleSubmit} noValidate>
              <InputLabel required>{t('service-center.get-update.serial.label')}</InputLabel>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <TextField name="serial" required />
                <OnChange name="serial">{(value) => setSerial(value)}</OnChange>
                <Button
                  color="secondary"
                  disabled={loading}
                  type="submit"
                  sx={{
                    minWidth: 'fit-content',
                    marginLeft: 2,
                  }}
                >
                  {t('service-center.get-update.serial.submit')}
                </Button>
              </Box>

              {submitError && !dirtySinceLastSubmit && (
                <Trans
                  i18nKey="service-center.get-update.serial.not-found"
                  parent={Alert}
                  severity="error"
                  sx={{ my: 1 }}
                  values={{ serial }}
                />
              )}
              {loading && <LinearProgress color="primary" sx={{ my: 1 }} />}

              {updateURL && !dirtySinceLastSubmit && (
                <Trans
                  fullWidth
                  href={updateURL}
                  i18nKey="service-center.get-update.serial.download"
                  parent={Button}
                  values={{ serial }}
                  sx={{ mt: 2 }}
                />
              )}
            </form>
          )}
        />
      </CardContent>
    </Card>
  );
};

export default GetConfigurationUpdateForm;
